import React, { useState, useEffect, useId } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { LoadingSpinnerRing } from './common/AppLoadingState';
import ThemeSegmentedControl from './ThemeSegmentedControl';

const FORGOT_MAIL =
  'mailto:info@lifelikeagents.com?subject=Password%20reset%20help';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const emailId = useId();
  const passwordId = useId();

  useEffect(() => {
    if (user) {
      if (user.isAdmin) {
        navigate('/app/admin');
      } else {
        navigate('/app');
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await api.login(formData.email, formData.password);
      login(userData, rememberMe);
      toast.success('Successfully logged in');

      if (userData.isAdmin) {
        navigate('/app/admin');
      } else {
        navigate('/app');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to log in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const inputClass =
    'block w-full rounded-xl border px-3.5 py-3 text-[15px] shadow-sm transition-[border-color,box-shadow,background-color] duration-150 ' +
    'border-[var(--app-field-border)] bg-[var(--app-field-bg)] text-[var(--app-ink)] ' +
    'placeholder:text-[var(--app-muted)] placeholder:opacity-90 ' +
    'focus:border-[var(--app-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--app-accent-ring)] ' +
    'dark:border-white/[0.12] dark:placeholder:text-slate-400 dark:focus:border-teal-400/70 dark:focus:ring-teal-400/25';

  return (
    <div
      className="relative min-h-screen overflow-x-hidden text-[var(--app-ink)]"
      style={{
        fontFamily:
          "ui-sans-serif, 'Bricolage Grotesque', system-ui, sans-serif",
      }}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background:
              'radial-gradient(ellipse 120% 80% at 15% -10%, rgba(15, 118, 110, 0.22), transparent 52%), radial-gradient(ellipse 90% 60% at 95% 15%, rgba(245, 158, 11, 0.18), transparent 48%), linear-gradient(165deg, #faf8f4 0%, #f0ebe3 45%, #e8f4f2 100%)',
          }}
        />
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background:
              'radial-gradient(ellipse 100% 70% at 10% 0%, rgba(45, 212, 191, 0.14), transparent 45%), radial-gradient(ellipse 80% 55% at 90% 10%, rgba(245, 158, 11, 0.09), transparent 42%), radial-gradient(ellipse 70% 50% at 50% 100%, rgba(15, 118, 110, 0.08), transparent 55%), #090c10',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.35] dark:opacity-[0.2]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(15, 118, 110, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 118, 110, 0.06) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      <header className="relative z-10 flex items-center justify-between gap-4 px-4 py-5 sm:px-8">
        <Link
          to="/"
          className="group inline-flex items-center gap-2 text-sm font-semibold text-[var(--app-muted)] transition-colors hover:text-[var(--app-accent)] dark:hover:text-teal-300"
        >
          <span
            className="rounded-lg bg-[var(--app-accent-soft)] px-2 py-1 font-['IBM_Plex_Mono',monospace] text-xs uppercase tracking-wider text-[var(--app-accent)] dark:bg-teal-400/10 dark:text-teal-300"
            aria-hidden
          >
            LA
          </span>
          <span className="text-[var(--app-ink)] dark:text-slate-100">
            Lifelike Agents
          </span>
        </Link>
        <ThemeSegmentedControl className="shrink-0" />
      </header>

      <main className="relative z-10 mx-auto flex max-w-6xl flex-col gap-10 px-4 pb-16 pt-6 sm:px-8 lg:flex-row lg:items-center lg:gap-16 lg:pb-24 lg:pt-10">
        <section className="mx-auto max-w-md flex-1 lg:mx-0 lg:max-w-lg">
          <p className="font-['IBM_Plex_Mono',monospace] text-xs font-medium uppercase tracking-[0.2em] text-[var(--app-accent)] dark:text-teal-300/90">
            Secure access
          </p>
          <h1 className="mt-3 text-balance text-3xl font-bold leading-tight tracking-tight text-[var(--app-ink)] dark:text-slate-50 sm:text-4xl">
            Welcome back to your workspace
          </h1>
          <p className="mt-4 text-pretty text-base leading-relaxed text-[var(--app-muted)] dark:text-slate-400">
            Sign in to manage agents, campaigns, and conversations. Your session
            stays encrypted in transit.
          </p>
          <ul className="mt-8 hidden space-y-3 text-sm text-[var(--app-muted)] dark:text-slate-400 lg:block">
            <li className="flex gap-3">
              <span
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--app-accent)] dark:bg-teal-400"
                aria-hidden
              />
              Voice AI flows, batch calling, and web agents in one hub.
            </li>
            <li className="flex gap-3">
              <span
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/80"
                aria-hidden
              />
              Enterprise-ready routing with fast redirects for admins.
            </li>
          </ul>
        </section>

        <section className="mx-auto w-full max-w-md flex-1">
          <div
            className="rounded-2xl border p-8 shadow-[var(--app-panel-shadow)] backdrop-blur-xl sm:p-10"
            style={{
              borderColor: 'var(--app-border)',
              background:
                'linear-gradient(145deg, var(--app-surface-strong), var(--app-surface-veil))',
            }}
          >
            <div className="text-center lg:text-left">
              <h2 className="text-xl font-bold text-[var(--app-ink)] dark:text-slate-50">
                Sign in to your account
              </h2>
              <p className="mt-2 text-sm text-[var(--app-muted)] dark:text-slate-400">
                Don&apos;t have an account?{' '}
                <Link
                  to="/request-demo"
                  className="font-semibold text-[var(--app-accent)] underline-offset-2 transition-colors hover:underline dark:text-teal-400 dark:hover:text-teal-300"
                >
                  Book a demo to get started
                </Link>
              </p>
            </div>

            <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
              <div>
                <label
                  htmlFor={emailId}
                  className="mb-1.5 block text-sm font-medium text-[var(--app-ink)] dark:text-slate-200"
                >
                  Email address
                </label>
                <input
                  id={emailId}
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={inputClass}
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div>
                <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
                  <label
                    htmlFor={passwordId}
                    className="block text-sm font-medium text-[var(--app-ink)] dark:text-slate-200"
                  >
                    Password
                  </label>
                  <a
                    href={FORGOT_MAIL}
                    className="text-xs font-semibold text-[var(--app-accent)] underline-offset-2 transition-colors hover:underline dark:text-teal-400 dark:hover:text-teal-300"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    id={passwordId}
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className={`${inputClass} pr-12`}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-[var(--app-muted)] transition-colors hover:bg-black/[0.04] hover:text-[var(--app-ink)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)] dark:hover:bg-white/[0.06] dark:hover:text-slate-200"
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <HiEyeOff className="h-5 w-5" aria-hidden />
                    ) : (
                      <HiEye className="h-5 w-5" aria-hidden />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="login-remember"
                  name="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-[var(--app-field-border)] text-[var(--app-accent)] focus:ring-[var(--app-accent-ring)] dark:border-slate-600 dark:bg-slate-900"
                />
                <label
                  htmlFor="login-remember"
                  className="select-none text-sm text-[var(--app-muted)] dark:text-slate-400"
                >
                  Keep me signed in on this device
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="relative flex w-full items-center justify-center overflow-hidden rounded-xl bg-[var(--app-accent)] px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-teal-900/15 transition-[transform,background-color,box-shadow] duration-150 hover:bg-[var(--app-accent-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--app-accent-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-bg-canvas)] disabled:cursor-not-allowed disabled:opacity-70 dark:shadow-teal-950/40 dark:focus-visible:ring-offset-slate-900"
              >
                {loading ? (
                  <span className="flex items-center gap-3">
                    <LoadingSpinnerRing
                      tone="onDark"
                      className="!h-5 !w-5 shrink-0"
                    />
                    Signing in…
                  </span>
                ) : (
                  <span>Sign in</span>
                )}
              </button>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Login;
